import type { Font } from 'opentype.js';
import { getPalette } from '@/catalogs/palettes';
import { getTemplate, type Template, type TextSlot } from '@/catalogs/templates';
import type { QrMatrix } from '@/core/qr/encode';
import { MIN_QUIET_ZONE, type Project, type TextAlign } from '@/core/project/types';
import type { Fill, LayoutWarning, Scene, SceneNode } from '@/core/render/scene';
import { lineToPathData, measureBlock, missingGlyphs, type TextBlockMetrics } from '@/core/render/text';
import {
  COMFORTABLE_MODULE_PX,
  contrastRatio,
  fitQr,
  isSafeHexColor,
  MIN_MODULE_PX,
  rectsOverlap,
  type Rect,
} from './geometry';

export type BuildInput = {
  project: Project;
  matrix: QrMatrix;
  /** Шрифт нужен, только если в шаблоне есть непустые текстовые блоки. */
  font: Font | null;
};

type TextBlock = {
  slot: TextSlot;
  text: string;
  size: number;
  metrics: TextBlockMetrics;
};

const round = Math.round;

function textFor(project: Project, slot: TextSlot): string {
  if (slot === 'title') return project.title;
  if (slot === 'caption') return project.caption.text;
  return project.note;
}

function sizeFor(project: Project, template: Template, slot: TextSlot): number {
  const base = project.caption.size;
  if (slot === 'title') return base * template.titleScale;
  if (slot === 'note') return base * template.noteScale;
  return base;
}

function alignX(align: TextAlign, boxX: number, boxW: number, itemW: number): number {
  if (align === 'left') return boxX;
  if (align === 'right') return boxX + boxW - itemW;
  return boxX + (boxW - itemW) / 2;
}

function qrWarnings(project: Project, moduleSize: number): LayoutWarning[] {
  const warnings: LayoutWarning[] = [];
  if (moduleSize < 1) {
    warnings.push({
      level: 'error',
      code: 'module-too-small',
      message:
        'При выбранном размере на один модуль приходится меньше одного пикселя. Увеличьте размер файла или сократите данные.',
    });
  } else if (moduleSize < MIN_MODULE_PX) {
    warnings.push({
      level: 'error',
      code: 'module-below-minimum',
      message: `Модуль занимает ${moduleSize} px. Мы не отдаём такие файлы: ниже ${MIN_MODULE_PX} px изображение почти наверняка не считается.`,
    });
  } else if (moduleSize < COMFORTABLE_MODULE_PX) {
    warnings.push({
      level: 'warning',
      code: 'module-small',
      message: `Модуль занимает ${moduleSize} px. Для печати и мелких экранов это мало — проверьте результат на реальном устройстве.`,
    });
  }

  if (!isSafeHexColor(project.qr.foreground) || !isSafeHexColor(project.qr.background)) {
    warnings.push({
      level: 'error',
      code: 'color-invalid',
      message: 'Цвет QR задан в неподдерживаемом формате. Допустимы только значения вида #RRGGBB.',
    });
  } else {
    const ratio = contrastRatio(project.qr.foreground, project.qr.background);
    if (ratio < 3) {
      warnings.push({
        level: 'error',
        code: 'contrast-low',
        message: `Контраст модулей и фона ${ratio.toFixed(1)}:1 — слишком мало. Это эвристика, а не стандарт сертификации, но при таком контрасте сканеры обычно отказываются читать код.`,
      });
    } else if (ratio < 7) {
      warnings.push({
        level: 'warning',
        code: 'contrast-modest',
        message: `Контраст модулей и фона ${ratio.toFixed(1)}:1. Безопасный вариант — тёмные модули на белом фоне.`,
      });
    }
  }

  if (project.qr.quietZoneModules < MIN_QUIET_ZONE) {
    warnings.push({
      level: 'warning',
      code: 'quiet-zone-small',
      message: `Защитное поле меньше ${MIN_QUIET_ZONE} модулей. Используем минимально допустимое значение ${MIN_QUIET_ZONE}.`,
    });
  }
  return warnings;
}

function sceneMeta(
  matrix: QrMatrix,
  moduleSize: number,
  quiet: number,
  description: string,
): Scene['meta'] {
  return {
    moduleSize,
    matrixSize: matrix.size,
    quietZoneModules: quiet,
    version: matrix.version,
    errorCorrection: matrix.errorCorrection,
    payload: matrix.source,
    description,
  };
}

/** Сцена «только QR»: поле, защитная зона и матрица, без всякого оформления. */
function buildQrOnly(project: Project, matrix: QrMatrix): Scene {
  const size = project.output.widthPx;
  const quiet = Math.max(MIN_QUIET_ZONE, project.qr.quietZoneModules);
  const fit = fitQr(size, matrix.size, quiet);
  const offset = round(fit.slackPx / 2);
  const qrRect: Rect = { x: offset, y: offset, w: fit.sizePx, h: fit.sizePx };

  const nodes: SceneNode[] = [];
  const backdrop: Rect = project.output.transparentBackground
    ? qrRect
    : { x: 0, y: 0, w: size, h: size };
  nodes.push({
    type: 'rect',
    rect: backdrop,
    radius: 0,
    fill: { kind: 'solid', color: project.qr.background },
  });
  nodes.push({
    type: 'qr',
    rect: qrRect,
    moduleSize: fit.moduleSize,
    quietZoneModules: quiet,
    matrix: matrix.modules,
    dark: project.qr.foreground,
    light: project.qr.background,
  });

  return {
    width: size,
    height: size,
    transparent: project.output.transparentBackground,
    nodes,
    qrSafeArea: qrRect,
    decorations: [],
    warnings: qrWarnings(project, fit.moduleSize),
    meta: sceneMeta(matrix, fit.moduleSize, quiet, 'QR-код без оформления'),
  };
}

/**
 * Основной расчёт макета: проект плюс матрица плюс шрифт — на выходе сцена.
 * Функция чистая: ни DOM, ни сети, ни глобального состояния.
 */
export function buildScene({ project, matrix, font }: BuildInput): Scene {
  if (!project.output.includeDecoration) return buildQrOnly(project, matrix);

  const template = getTemplate(project.template.id);
  const palette = getPalette(project.paletteId);
  const W = project.output.widthPx;
  const quiet = Math.max(MIN_QUIET_ZONE, project.qr.quietZoneModules);
  const warnings: LayoutWarning[] = [];

  const pad = round(template.padding * W);
  const gap = round(template.gap * W);
  const frameWidth = template.frame ? Math.max(1, round(template.frame.width * W)) : 0;
  const barSize = template.accentBar ? round(template.accentBar.thickness * W) : 0;
  const cardRadius = round(template.cardRadius * W);

  const topInset = (template.accentBar?.position === 'top' ? barSize : 0) + pad;
  const leftInset = (template.accentBar?.position === 'left' ? barSize : 0) + pad;
  const bottomInset = (template.accentBar?.position === 'bottom' ? barSize : 0) + pad;
  const contentX = leftInset;
  const contentW = W - leftInset - pad;

  const qrAvailable = template.direction === 'row' ? Math.floor(contentW * template.qrShare) : contentW;
  const fit = fitQr(qrAvailable, matrix.size, quiet);
  warnings.push(...qrWarnings(project, fit.moduleSize));

  const requested = template.blocks
    .filter((b): b is { kind: TextSlot } => b.kind !== 'qr')
    .map((b) => ({ slot: b.kind, text: textFor(project, b.kind), size: sizeFor(project, template, b.kind) }))
    .filter((c) => c.text.trim() !== '');

  if (requested.length > 0 && !font) {
    warnings.push({
      level: 'info',
      code: 'font-loading',
      message: 'Шрифт ещё загружается — подпись появится в макете, как только он будет готов.',
    });
  }

  const textBoxW =
    template.direction === 'row' ? Math.max(1, contentW - fit.sizePx - gap) : Math.max(1, contentW);

  const blocks: TextBlock[] = [];
  if (font) {
    for (const item of requested) {
      const missing = missingGlyphs(font, item.text);
      if (missing.length > 0) {
        warnings.push({
          level: 'error',
          code: 'missing-glyphs',
          message: `В выбранном шрифте нет символов: ${missing.join(' ')}. Выберите другой шрифт или измените текст — подменять шрифт молча мы не будем.`,
        });
      }
      blocks.push({ ...item, metrics: measureBlock(font, item.text, item.size, textBoxW) });
    }
  }

  const blockBySlot = new Map(blocks.map((b) => [b.slot, b]));
  const plateFor = (slot: TextSlot): boolean => template.plate?.target === slot;
  const platePadY = (size: number): number => round(size * 0.35);

  /** Высота блока с учётом плашки, если она есть. */
  const blockHeight = (b: TextBlock): number =>
    b.metrics.height + (plateFor(b.slot) ? platePadY(b.size) * 2 : 0);

  // --- размеры документа --------------------------------------------------
  let height: number;
  let qrRect: Rect;
  let textTop: number;
  let textBoxX: number;

  if (template.direction === 'row') {
    const stack = blocks.reduce((sum, b, i) => sum + blockHeight(b) + (i > 0 ? gap : 0), 0);
    const bodyH = Math.max(fit.sizePx, stack);
    height = topInset + bodyH + bottomInset;
    qrRect = {
      x: contentX + round((qrAvailable - fit.sizePx) / 2),
      y: topInset + round((bodyH - fit.sizePx) / 2),
      w: fit.sizePx,
      h: fit.sizePx,
    };
    textTop = topInset + round((bodyH - stack) / 2);
    textBoxX = contentX + qrAvailable + gap;
  } else {
    const flow = template.blocks
      .map((b) => (b.kind === 'qr' ? fit.sizePx : (blockBySlot.get(b.kind) ? blockHeight(blockBySlot.get(b.kind)!) : 0)))
      .filter((h) => h > 0);
    height = topInset + flow.reduce((a, b) => a + b, 0) + Math.max(0, flow.length - 1) * gap + bottomInset;

    let cursor = topInset;
    let qrY = topInset;
    for (const b of template.blocks) {
      const h = b.kind === 'qr' ? fit.sizePx : (blockBySlot.get(b.kind) ? blockHeight(blockBySlot.get(b.kind)!) : 0);
      if (h === 0) continue;
      if (b.kind === 'qr') qrY = cursor;
      cursor += h + gap;
    }
    qrRect = {
      x: contentX + round((contentW - fit.sizePx) / 2),
      y: qrY,
      w: fit.sizePx,
      h: fit.sizePx,
    };
    textTop = topInset;
    textBoxX = contentX;
  }

  // Высота документа всегда целая: дробный размер canvas округляется
  // браузером и даёт сдвиг на пиксель между предпросмотром и файлом.
  height = Math.ceil(height);

  const nodes: SceneNode[] = [];
  const decorations: Rect[] = [];

  // --- фон карточки -------------------------------------------------------
  const cardFill: Fill = palette.backgroundGradient
    ? { kind: 'gradient', id: 'card-bg', from: palette.backgroundGradient[0], to: palette.backgroundGradient[1] }
    : { kind: 'solid', color: palette.background };

  if (!project.output.transparentBackground) {
    nodes.push({ type: 'rect', rect: { x: 0, y: 0, w: W, h: height }, radius: cardRadius, fill: cardFill });
  }

  if (template.accentBar) {
    const pos = template.accentBar.position;
    const bar: Rect =
      pos === 'left'
        ? { x: 0, y: 0, w: barSize, h: height }
        : pos === 'top'
          ? { x: 0, y: 0, w: W, h: barSize }
          : { x: 0, y: height - barSize, w: W, h: barSize };
    nodes.push({
      type: pos === 'top' ? 'topbar' : 'rect',
      rect: bar,
      radius: cardRadius,
      fill: { kind: 'solid', color: palette.accent },
    });
    decorations.push(bar);
  }

  if (template.frame) {
    const inset = round(pad * 0.4) + frameWidth / 2;
    const frameRect: Rect = { x: inset, y: inset, w: W - 2 * inset, h: height - 2 * inset };
    nodes.push({
      type: 'stroke',
      rect: frameRect,
      radius: round(template.frame.radius * W),
      color: palette.border,
      width: frameWidth,
    });
    // Для проверки защитного поля важны сами линии рамки, а не её внутренность.
    const half = frameWidth / 2;
    decorations.push(
      { x: frameRect.x - half, y: frameRect.y - half, w: frameRect.w + frameWidth, h: frameWidth },
      { x: frameRect.x - half, y: frameRect.y + frameRect.h - half, w: frameRect.w + frameWidth, h: frameWidth },
      { x: frameRect.x - half, y: frameRect.y - half, w: frameWidth, h: frameRect.h + frameWidth },
      { x: frameRect.x + frameRect.w - half, y: frameRect.y - half, w: frameWidth, h: frameRect.h + frameWidth },
    );
  }

  // --- поле QR ------------------------------------------------------------
  nodes.push({
    type: 'rect',
    rect: qrRect,
    radius: 0,
    fill: { kind: 'solid', color: project.qr.background },
  });
  nodes.push({
    type: 'qr',
    rect: qrRect,
    moduleSize: fit.moduleSize,
    quietZoneModules: quiet,
    matrix: matrix.modules,
    dark: project.qr.foreground,
    light: project.qr.background,
  });

  // --- текстовые блоки ----------------------------------------------------
  if (font && blocks.length > 0) {
    const align = project.caption.align;
    const order: TextBlock[] =
      template.direction === 'row'
        ? blocks
        : (template.blocks
            .map((b) => (b.kind === 'qr' ? null : blockBySlot.get(b.kind) ?? null))
            .filter((b): b is TextBlock => b !== null));

    let cursor = textTop;
    for (const block of order) {
      if (template.direction !== 'row') {
        // В колонке текст встаёт по потоку: до QR или после него.
        cursor = flowTopFor(block);
      }
      const padY = plateFor(block.slot) ? platePadY(block.size) : 0;
      if (plateFor(block.slot)) {
        const padX = round(block.size * 0.6);
        const plateW = Math.min(textBoxW, block.metrics.width + padX * 2);
        const plateRect: Rect = {
          x: alignX(align, textBoxX, textBoxW, plateW),
          y: cursor,
          w: plateW,
          h: block.metrics.height + padY * 2,
        };
        nodes.push({
          type: 'rect',
          rect: plateRect,
          radius: round(template.plate!.radius * W),
          fill: { kind: 'solid', color: palette.accent },
        });
        decorations.push(plateRect);
      }

      const textColor = plateFor(block.slot) ? palette.background : palette.text;
      block.metrics.lines.forEach((line, index) => {
        if (line.text === '') return;
        const top = cursor + padY + index * block.metrics.lineHeight;
        const x = alignX(align, textBoxX, textBoxW, line.width);
        const d = lineToPathData(font, line.text, x, top + block.metrics.ascender, block.size);
        if (d !== '') nodes.push({ type: 'path', d, fill: textColor });
        decorations.push({ x, y: top, w: line.width, h: block.metrics.lineHeight });
      });

      cursor += blockHeight(block) + gap;
    }

    function flowTopFor(target: TextBlock): number {
      let y = topInset;
      for (const b of template.blocks) {
        if (b.kind === 'qr') {
          if (fit.sizePx > 0) y += fit.sizePx + gap;
          continue;
        }
        const found = blockBySlot.get(b.kind);
        if (!found) continue;
        if (found === target) return y;
        y += blockHeight(found) + gap;
      }
      return y;
    }
  }

  // --- самопроверка защитного поля ---------------------------------------
  if (decorations.some((rect) => rectsOverlap(rect, qrRect))) {
    warnings.push({
      level: 'error',
      code: 'quiet-zone-violated',
      message:
        'Оформление залезает в защитное поле QR. Уменьшите кегль подписи или увеличьте размер документа.',
    });
  }

  const described = [project.title, project.caption.text, project.note]
    .filter((t) => t.trim() !== '')
    .join('. ');

  return {
    width: W,
    height,
    transparent: project.output.transparentBackground,
    nodes,
    qrSafeArea: qrRect,
    decorations,
    warnings,
    meta: sceneMeta(
      matrix,
      fit.moduleSize,
      quiet,
      described === '' ? 'QR-код' : `QR-код. ${described}`,
    ),
  };
}
